import {
  loadBitmapFontPackagePages,
  rasterizeBitmapFontGlyph,
} from '@/features/bitmap-font/generator'
import { formatCanvasFontFamily, measureChengguangBitmapGlyph } from '@/features/bitmap-font/metrics'
import { resolveBitmapFontRasterSize } from '@/features/bitmap-font/bitmapFontPadding'
import { clampBitmapFontSize } from '@/features/bitmap-font/fontSize'
import { findBitmapFontPackage } from '@/features/bitmap-font/runtime'
import { resolveMainFontSpec } from '@/features/bitmap-font/fontSpec'
import type { BitmapFontPackage } from '@/model/BitmapFont/BitmapFontData'
import type MainProject from '@/model/MainProject'
import type DataFileMap from '@/model/Resource/DataFileMap'

export type TalkBitmapMetrics = {
  fontFamily: string
  fontSize: number
  rasterFontSize: number
  cellWidth: number
  cellHeight: number
  aboveLine: number
  lineSpace: number
  metricKey: string
  glyphWidthByChar: Map<string, number>
  package: BitmapFontPackage | null
}

type TalkTextToken = {
  text: string
  color: string
}

const SPACE = ' '
const DEFAULT_SPACE_WIDTH = 6
const glyphBitmapCache = new Map<string, HTMLCanvasElement>()
const packagePageCache = new WeakMap<BitmapFontPackage, HTMLCanvasElement[]>()

function clampColorChannel(value: string): number {
  const next = Number.parseInt(value, 10)
  if (!Number.isFinite(next)) return 0
  return Math.max(0, Math.min(255, next))
}

function parseColorTokens(text: string, defaultColor: string): TalkTextToken[] {
  const tokens: TalkTextToken[] = []
  const colorTagPattern = /\\c\[\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\]/g
  let color = defaultColor
  let cursor = 0
  for (const match of text.matchAll(colorTagPattern)) {
    const index = match.index ?? 0
    if (index > cursor) {
      tokens.push({ text: text.slice(cursor, index), color })
    }
    color = `rgb(${clampColorChannel(match[1]!)}, ${clampColorChannel(match[2]!)}, ${clampColorChannel(match[3]!)})`
    cursor = index + match[0].length
  }
  if (cursor < text.length) {
    tokens.push({ text: text.slice(cursor), color })
  }
  return tokens.length ? tokens : [{ text, color: defaultColor }]
}

function liveCharAdvance(char: string, metrics: TalkBitmapMetrics): number {
  return measureChengguangBitmapGlyph(char, metrics.fontFamily, metrics.rasterFontSize).width
}

/**
 * 引擎行步进：floor(格子高 × 1.5)。
 * @param cellHeight xfi 格子高
 */
export function resolveTalkLineSpace(cellHeight: number): number {
  return Math.floor(Math.max(1, cellHeight) * 1.5)
}

/**
 * 单字前进宽（与 xfi `width` 同一口径；空格缺字时为 6）。
 */
export function measureTalkCharAdvance(
  char: string,
  metrics: TalkBitmapMetrics,
): number {
  if (!char) return 0
  const packed = metrics.glyphWidthByChar.get(char)
  if (packed && packed > 0) return packed
  const measured = liveCharAdvance(char, metrics)
  if (char === SPACE) return measured > 0 ? measured : DEFAULT_SPACE_WIDTH
  return measured
}

/**
 * 解析当前工程对话主字体的位图排版参数。
 */
export function resolveTalkBitmapMetrics(
  project: Pick<MainProject, 'systemData' | 'bitmapFont'> | null | undefined,
  fontRoot: DataFileMap | null | undefined,
  fontFamily: string,
): TalkBitmapMetrics | null {
  if (!project) return null
  const spec = resolveMainFontSpec(project as MainProject, fontRoot ?? null)
  const nominal = clampBitmapFontSize(spec.fontSize)
  const rasterFontSize = resolveBitmapFontRasterSize(nominal)
  const family = String(fontFamily || '').trim() || formatCanvasFontFamily(spec.sourceFontName)
  const pkg: BitmapFontPackage | null = findBitmapFontPackage(project as MainProject, spec.packageFontName, nominal)
  const glyphWidthByChar = new Map<string, number>()
  if (pkg) {
    for (const glyph of pkg.glyphs) {
      if (!glyph.char) continue
      glyphWidthByChar.set(glyph.char, glyph.width > 0 ? glyph.width : pkg.cellWidth)
    }
  }
  let cellWidth = pkg?.cellWidth ?? 0
  let cellHeight = pkg?.cellHeight ?? 0
  let aboveLine = pkg?.aboveLine ?? 0
  if (!cellWidth || !cellHeight) {
    const probe = measureChengguangBitmapGlyph('国', family, rasterFontSize)
    cellWidth = probe.width
    cellHeight = probe.height
    aboveLine = probe.aboveLine || nominal
  }
  return {
    fontFamily: family,
    fontSize: nominal,
    rasterFontSize,
    cellWidth,
    cellHeight,
    aboveLine,
    lineSpace: resolveTalkLineSpace(cellHeight),
    metricKey: `${family}|${nominal}|${rasterFontSize}|${cellWidth}|${cellHeight}|${aboveLine}|${pkg?.glyphs.length ?? 0}|${pkg?.files.length ?? 0}`,
    glyphWidthByChar,
    package: pkg,
  }
}

function glyphAtlasRect(
  pkg: BitmapFontPackage,
  glyphIndex: number,
): { page: number; x: number; y: number } {
  const cols = Math.max(1, pkg.cols)
  const rows = Math.max(1, pkg.rows)
  const slots = cols * rows
  const page = Math.floor(glyphIndex / slots)
  return {
    page,
    x: (glyphIndex % cols) * pkg.cellWidth,
    y: (Math.floor(glyphIndex / cols) - rows * page) * pkg.cellHeight,
  }
}

async function getPackagePages(pkg: BitmapFontPackage): Promise<HTMLCanvasElement[]> {
  const cached = packagePageCache.get(pkg)
  if (cached) return cached
  const pages = await loadBitmapFontPackagePages(pkg)
  packagePageCache.set(pkg, pages)
  return pages
}

function glyphCacheKey(metrics: TalkBitmapMetrics, char: string): string {
  return `${metrics.metricKey}|${char}`
}

function getLiveGlyphBitmap(metrics: TalkBitmapMetrics, char: string): HTMLCanvasElement {
  const key = glyphCacheKey(metrics, char)
  const cached = glyphBitmapCache.get(key)
  if (cached) return cached
  const raster = rasterizeBitmapFontGlyph({
    char,
    fontFamily: metrics.fontFamily,
    fontSize: metrics.rasterFontSize,
    cellWidth: metrics.cellWidth,
    cellHeight: metrics.cellHeight,
    aboveLine: metrics.aboveLine,
  })
  glyphBitmapCache.set(key, raster.bitmap)
  return raster.bitmap
}

function blitGlyphToTint(
  tintCtx: CanvasRenderingContext2D,
  tintCanvas: HTMLCanvasElement,
  source: HTMLCanvasElement,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  color: string,
): void {
  const srcX = sx | 0
  const srcY = sy | 0
  const width = Math.max(1, sw | 0)
  const height = Math.max(1, sh | 0)
  tintCtx.imageSmoothingEnabled = false
  tintCtx.clearRect(0, 0, tintCanvas.width, tintCanvas.height)
  tintCtx.globalCompositeOperation = 'source-over'
  tintCtx.drawImage(source, srcX, srcY, width, height, 0, 0, width, height)
  tintCtx.globalCompositeOperation = 'source-in'
  tintCtx.fillStyle = color
  tintCtx.fillRect(0, 0, width, height)
  tintCtx.globalCompositeOperation = 'source-over'
}

/**
 * 有位图包时从 xfi/png 取样（与测试同一份纹理）；缺字才用 3.0 同款 fillText 回退。
 */
export async function drawTalkBitmapText(params: {
  text: string
  defaultColor: string
  metrics: TalkBitmapMetrics
  singleLine?: boolean
}): Promise<{ canvas: HTMLCanvasElement; width: number; height: number }> {
  const chars: Array<{ char: string; color: string }> = []
  for (const token of parseColorTokens(params.text, params.defaultColor)) {
    for (const char of Array.from(token.text)) {
      if (char === '\n') {
        if (params.singleLine) break
        chars.push({ char: '\n', color: token.color })
        continue
      }
      chars.push({ char, color: token.color })
    }
    if (params.singleLine && chars.some((item) => item.char === '\n')) break
  }

  let drawX = 0
  let drawY = 0
  let maxX = 0
  const placed: Array<{ char: string; color: string; x: number; y: number }> = []
  for (const item of chars) {
    if (item.char === '\n') {
      drawX = 0
      drawY += params.metrics.lineSpace
      continue
    }
    const advance = measureTalkCharAdvance(item.char, params.metrics)
    if (item.char !== SPACE) {
      placed.push({ char: item.char, color: item.color, x: drawX, y: drawY })
    }
    drawX += advance
    maxX = Math.max(maxX, drawX)
  }

  const width = Math.max(1, Math.round(maxX))
  const height = Math.max(1, Math.round(drawY + params.metrics.cellHeight))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('对话位图绘制 canvas 创建失败')
  }
  ctx.imageSmoothingEnabled = false
  ctx.clearRect(0, 0, width, height)
  const tintCanvas = document.createElement('canvas')
  tintCanvas.width = params.metrics.cellWidth
  tintCanvas.height = params.metrics.cellHeight
  const tintCtx = tintCanvas.getContext('2d')
  if (!tintCtx) {
    throw new Error('对话位图着色 canvas 创建失败')
  }
  const pkg = params.metrics.package
  const pages = pkg ? await getPackagePages(pkg) : null
  const glyphByChar = new Map(pkg?.glyphs.map((item) => [item.char, item]) ?? [])
  for (const item of placed) {
    const glyph = glyphByChar.get(item.char)
    const destX = Math.round(item.x)
    const destY = Math.round(item.y)
    if (pkg && pages && glyph) {
      const rect = glyphAtlasRect(pkg, glyph.index)
      const page = pages[rect.page]
      if (page) {
        const sw = Math.max(1, glyph.width || pkg.cellWidth)
        const sh = Math.max(1, glyph.height || pkg.cellHeight)
        blitGlyphToTint(tintCtx, tintCanvas, page, rect.x, rect.y, sw, sh, item.color)
        ctx.drawImage(tintCanvas, 0, 0, sw, sh, destX, destY, sw, sh)
        continue
      }
    }
    const live = getLiveGlyphBitmap(params.metrics, item.char)
    blitGlyphToTint(tintCtx, tintCanvas, live, 0, 0, live.width, live.height, item.color)
    ctx.drawImage(tintCanvas, 0, 0, live.width, live.height, destX, destY, live.width, live.height)
  }
  return { canvas, width, height }
}

/**
 * 姓名/单行位图宽度（不含换行）。
 */
export function measureTalkRunWidth(text: string, metrics: TalkBitmapMetrics): number {
  let width = 0
  for (const token of parseColorTokens(text, '')) {
    for (const char of Array.from(token.text)) {
      if (char === '\n') continue
      width += measureTalkCharAdvance(char, metrics)
    }
  }
  return width
}
